import { useCallback, useEffect, useMemo, useRef } from "react";
import {
    PresenceScope,
    PresenceStatus,
    PresenceTargetParams,
    TASK_PRESENCE_TTL_MS,
    getPresenceHeartbeatSubject,
    getPresenceProbeTarget,
    getPresenceSubscriptionSubject,
    getPresenceTtlMs,
    parsePresenceHeartbeatSubject,
} from "@common/types/presence-nats";
import { useUser } from "@hooks/useUser";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { getPresenceHeartbeatIntervalMs } from "./presence.lib";
import { usePresenceConnection } from "./usePresenceConnection";

type PresenceMode = "keepAlive" | "onDemand";

interface UsePublishPresenceOptions {
    /**
     * `keepAlive` claims on mount, re-sends while the tab is visible and
     * answers observers that ask who is present. `onDemand` only sends when
     * the caller invokes `heartbeat`.
     */
    mode: PresenceMode;
}

const PROBE_RESPONSE_MIN_GAP_MS = 1_000;

const isTabVisible = () => document.visibilityState === "visible";

/**
 * Announces that the current user is present on a target, with a heartbeat
 * that expires on its own when this client goes away.
 */
export const usePublishPresence = (
    target: PresenceTargetParams | null,
    { mode }: UsePublishPresenceOptions,
) => {
    const currentUserId = useUser()?.id;
    const lastSentAtRef = useRef(0);

    const organizationId = target?.organizationId;
    const workspaceId = target?.workspaceId;
    const kind = target?.kind;
    const targetId = target?.targetId;

    const scope = useMemo((): PresenceScope | null => {
        if (organizationId) {
            return { organizationId };
        }
        return workspaceId ? { workspaceId } : null;
    }, [organizationId, workspaceId]);
    const connection = usePresenceConnection(scope);
    const heartbeatIntervalMs = getPresenceHeartbeatIntervalMs(
        kind ? getPresenceTtlMs(kind) : TASK_PRESENCE_TTL_MS,
    );

    const heartbeatSubject = useMemo(
        () =>
            workspaceId && kind && targetId && currentUserId !== undefined
                ? getPresenceHeartbeatSubject({
                      organizationId,
                      workspaceId,
                      kind,
                      targetId,
                      userId: currentUserId,
                  })
                : null,
        [organizationId, workspaceId, kind, targetId, currentUserId],
    );
    const probeSubject = useMemo(
        () =>
            mode === "keepAlive" && scope && kind
                ? getPresenceSubscriptionSubject(
                      getPresenceProbeTarget({ scope, observedKind: kind }),
                  )
                : null,
        [mode, scope, kind],
    );

    const publish = useCallback(
        (status: PresenceStatus) => {
            if (!connection || !heartbeatSubject) {
                return;
            }

            try {
                connection.publish(
                    heartbeatSubject,
                    JSON.stringify({ status }),
                );
                lastSentAtRef.current = Date.now();
            } catch {
                // Best-effort: a closed connection is re-established by its provider
            }
        },
        [connection, heartbeatSubject],
    );

    useNatsSubscription(probeSubject, {
        connection,
        onMessage: (_payload, message) => {
            const observer = parsePresenceHeartbeatSubject(message.subject);
            const hasRecentlyAnswered =
                Date.now() - lastSentAtRef.current < PROBE_RESPONSE_MIN_GAP_MS;
            const shouldAnswer =
                !!observer &&
                observer.userId !== currentUserId &&
                !hasRecentlyAnswered &&
                isTabVisible();
            if (shouldAnswer) {
                publish(PresenceStatus.ACTIVE);
            }
        },
    });

    const heartbeat = useCallback(() => {
        const isThrottled =
            Date.now() - lastSentAtRef.current < heartbeatIntervalMs;
        if (!isThrottled) {
            publish(PresenceStatus.ACTIVE);
        }
    }, [heartbeatIntervalMs, publish]);

    useEffect(
        function keepPresenceAlive() {
            if (mode !== "keepAlive" || !heartbeatSubject) {
                return;
            }

            const beatWhileVisible = () => {
                if (isTabVisible()) {
                    publish(PresenceStatus.ACTIVE);
                }
            };

            if (isTabVisible()) {
                publish(PresenceStatus.JOINED);
            }
            const timerId = window.setInterval(
                beatWhileVisible,
                heartbeatIntervalMs,
            );
            document.addEventListener("visibilitychange", beatWhileVisible);

            return () => {
                window.clearInterval(timerId);
                document.removeEventListener(
                    "visibilitychange",
                    beatWhileVisible,
                );
                publish(PresenceStatus.LEFT);
            };
        },
        [mode, heartbeatSubject, heartbeatIntervalMs, publish],
    );

    return { heartbeat };
};
